import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatError } from "../lib/api";
import { fileUrl } from "../lib/seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Trash2, Upload, Truck } from "lucide-react";
import { PRODUCT_CATS } from "./Shop";

const EMPTY = { title: "", description: "", category: "rudraksha", sku: "", price_usd: 10, stock: 10, image_urls: [] };

export default function SupplierDashboard() {
  const { user, loading } = useAuth();
  const [me, setMe] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const refresh = () => {
    api.get("/shop/supplier/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/shop/supplier/products").then((r) => setProducts(r.data.items)).catch(() => {});
    api.get("/shop/supplier/orders").then((r) => setOrders(r.data.items)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "supplier") refresh(); }, [user]);

  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "supplier") return <Navigate to="/dashboard" replace />;
  const approved = ["approved", "onboarded"].includes(me?.supplier?.status);

  return (
    <div data-testid="supplier-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="overline">{me?.supplier?.business_name}</div>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <h1 className="font-serif text-4xl sm:text-5xl text-[#4A0E17]">Supplier Dashboard</h1>
        {me && <span data-testid="supplier-status" className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${approved ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{me.supplier.status}</span>}
      </div>
      {me && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-10">
          {[["Live products", me.product_count], ["Paid orders", me.stats.count], ["Gross sales", `$${(me.stats.gross || 0).toFixed(2)}`], ["Your earnings (85%)", `$${(me.stats.earned || 0).toFixed(2)}`]].map(([l, v]) => (
            <div key={l} className="bg-[#FFFDF9] sacred-border rounded-2xl p-5"><div className="font-serif text-3xl text-[#4A0E17]">{v}</div><div className="overline mt-1">{l}</div></div>
          ))}
        </div>
      )}
      {!approved && me && <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">Your account is waiting for admin approval. You can add products once you're approved.</div>}
      <section className="mt-14 grid lg:grid-cols-2 gap-8">
        <ProductForm disabled={!approved} onSaved={refresh} />
        <div>
          <h2 className="font-serif text-2xl text-[#4A0E17]">Your products</h2>
          <div className="mt-5 space-y-3">
            {products.length === 0 && <div className="text-[#63534E] text-sm">No products yet.</div>}
            {products.map((p) => (
              <div key={p.id} data-testid={`my-product-${p.id}`} className="flex items-center gap-4 bg-[#FFFDF9] sacred-border rounded-xl p-3">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#F3E5AB]/40 shrink-0">{p.image_urls?.[0] && <img src={fileUrl(p.image_urls[0])} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-0"><div className="font-semibold text-[#4A0E17] truncate">{p.title}</div><div className="text-xs text-[#63534E]">${p.price_usd.toFixed(2)} · {p.stock} in stock</div></div>
                <button data-testid={`del-product-${p.id}`} onClick={async () => { await api.delete(`/shop/supplier/products/${p.id}`); toast.success("Removed"); refresh(); }} className="p-2 text-[#8C6D3B] hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <OrdersTable orders={orders} onChange={refresh} />
    </div>
  );
}

function ProductForm({ disabled, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/uploads/image", fd); setF((x) => ({ ...x, image_urls: [...x.image_urls, data.url] })); }
    catch (err) { toast.error(formatError(err)); }
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/shop/supplier/products", { ...f, price_usd: parseFloat(f.price_usd), stock: parseInt(f.stock), sku: f.sku || null });
      toast.success("Product is live in the shop"); setF(EMPTY); onSaved();
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} data-testid="product-form" className="bg-[#FFFDF9] sacred-border rounded-2xl p-6 space-y-3 h-fit">
      <h2 className="font-serif text-2xl text-[#4A0E17]">Add a product</h2>
      <Input data-testid="pf-title" required placeholder="Product title" value={f.title} onChange={set("title")} className="bg-white border-[#D4AF37]/40 h-11" />
      <Textarea data-testid="pf-description" required rows={3} placeholder="Description, materials, authenticity…" value={f.description} onChange={set("description")} className="bg-white border-[#D4AF37]/40" />
      <div className="grid grid-cols-2 gap-3">
        <select data-testid="pf-category" value={f.category} onChange={set("category")} className="h-11 rounded-md border border-[#D4AF37]/40 bg-white px-3 text-sm">{PRODUCT_CATS.map((c) => <option key={c.k} value={c.k}>{c.l}</option>)}</select>
        <Input data-testid="pf-sku" placeholder="SKU (optional)" value={f.sku} onChange={set("sku")} className="bg-white border-[#D4AF37]/40 h-11" />
        <Input data-testid="pf-price" type="number" step="0.01" min="0.5" placeholder="Price USD" value={f.price_usd} onChange={set("price_usd")} className="bg-white border-[#D4AF37]/40 h-11" />
        <Input data-testid="pf-stock" type="number" min="0" placeholder="Stock" value={f.stock} onChange={set("stock")} className="bg-white border-[#D4AF37]/40 h-11" />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {f.image_urls.map((u) => <img key={u} src={fileUrl(u)} alt="" className="w-14 h-14 rounded-lg object-cover" />)}
        <label className="w-14 h-14 rounded-lg border-2 border-dashed border-[#D4AF37] flex items-center justify-center cursor-pointer text-[#8C6D3B]">
          <Upload className="w-4 h-4" /><input data-testid="pf-image" type="file" accept="image/*" onChange={upload} className="hidden" />
        </label>
      </div>
      <Button data-testid="pf-submit" disabled={disabled || busy} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-11">{busy ? "Saving…" : "Publish product"}</Button>
    </form>
  );
}

function OrdersTable({ orders, onChange }) {
  const ship = async (o) => {
    const tracking = window.prompt("Tracking number / courier (optional)") ?? "";
    try { await api.put(`/shop/supplier/orders/${o.session_id}/ship`, { tracking }); toast.success("Marked shipped"); onChange(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <section className="mt-14">
      <h2 className="font-serif text-2xl text-[#4A0E17]">Orders</h2>
      <div className="mt-5 bg-[#FFFDF9] sacred-border rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>{["Product", "Buyer", "Amount", "Payment", "Fulfilment", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#63534E]">No orders yet.</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-[#D4AF37]/20 align-top">
                <td className="px-4 py-3">{o.product_title} × {o.quantity}</td>
                <td className="px-4 py-3">{o.donor_name}<div className="text-xs text-[#63534E] max-w-[220px]">{o.shipping_address}</div></td>
                <td className="px-4 py-3 font-mono">${o.amount?.toFixed(2)}</td>
                <td className="px-4 py-3 capitalize">{o.payment_status}</td>
                <td className="px-4 py-3 capitalize">{(o.fulfillment_status || "").replace(/_/g, " ")}{o.tracking ? ` · ${o.tracking}` : ""}</td>
                <td className="px-4 py-3">{o.payment_status === "paid" && o.fulfillment_status !== "shipped" && <Button data-testid={`ship-${o.session_id}`} onClick={() => ship(o)} className="h-8 text-xs rounded-full bg-[#E65100] hover:bg-[#BF360C]"><Truck className="w-3 h-3 mr-1" /> Ship</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
